/**
 * Login page — single screen with two stages driven by local state:
 *
 *   Stage A (idle):     "Login with Telegram" CTA.
 *   Stage B (otp):      6-digit OTP input + verify button.
 *
 * Auth flow:
 *   1. User clicks the CTA → `requestOtp()` (shared hook):
 *      - generates a session UUID
 *      - persists it to localStorage and the URL hash (so a refresh
 *        between the bot redirect and code entry never loses it)
 *      - opens the bot deeplink: t.me/TurniflyBot?start=login_<sid>
 *   2. The bot sends a 6-digit code to the user via Telegram message.
 *   3. User returns to this tab, types the code, hits "Verify".
 *   4. `verifyOtp(code)` calls /auth/verify-code, persists tokens,
 *      and flips the auth store → router redirects to "/".
 */

import type { ReactNode } from 'react'
import { useState } from 'react'
import { useTranslation } from 'react-i18next'
import { Send, ArrowRight, RotateCcw } from 'lucide-react'
import { useOtpAuth } from '@turnifly/shared/hooks'
import { Button, Logo } from '@turnifly/shared/ui'
import { triggerHapticImpact, triggerHapticNotification } from '@turnifly/shared/utils'

type Stage = 'idle' | 'otp'

const OTP_LENGTH = 6

export function LoginPage(): ReactNode {
  const { t } = useTranslation()
  const { requestOtp, verifyOtp, isLoading, error, clearError } = useOtpAuth()
  const [stage, setStage] = useState<Stage>('idle')
  const [code, setCode] = useState('')

  const handleRequest = (): void => {
    triggerHapticImpact('light')
    clearError()
    requestOtp()
    setStage('otp')
  }

  const handleResend = (): void => {
    triggerHapticImpact('light')
    clearError()
    setCode('')
    requestOtp()
  }

  const handleCodeChange = (raw: string): void => {
    // Keep only digits, cap at OTP_LENGTH
    const digits = raw.replace(/\D/g, '').slice(0, OTP_LENGTH)
    setCode(digits)
    if (error) clearError()
  }

  const handleVerify = async (): Promise<void> => {
    if (code.length !== OTP_LENGTH) return
    triggerHapticImpact('medium')
    const ok = await verifyOtp(code)
    if (ok) {
      triggerHapticNotification('success')
      // App.tsx auth gate notices isAuthenticated flip and the auth-layout
      // <Navigate to="/" replace /> takes us home automatically.
    } else {
      triggerHapticNotification('error')
    }
  }

  return (
    <div className="flex flex-col items-center gap-8 animate-[fadeIn_0.5s_ease-out]">
      <Logo size="lg" />

      <div className="text-center">
        <h1 className="font-display font-bold text-2xl text-text mb-2">
          Turnifly
        </h1>
        <p className="text-text-hint text-sm">
          {stage === 'idle' ? t('auth.loginSubtitle', 'Hisobingizga kiring') : t('auth.otpSubtitle', 'Telegramda yuborilgan kodni kiriting')}
        </p>
      </div>

      {stage === 'idle' && (
        <div className="w-full flex flex-col gap-3 mt-2">
          <Button
            variant="primary"
            size="lg"
            fullWidth
            onClick={handleRequest}
            isLoading={isLoading}
          >
            <Send size={18} className="mr-2" />
            {t('auth.loginWithTelegram', 'Telegram orqali kirish')}
          </Button>
          <p className="text-xs text-text-hint/60 text-center px-4">
            {t('auth.loginHint', 'Botda 6 xonali kod yuboriladi.')}
          </p>
        </div>
      )}

      {stage === 'otp' && (
        <div className="w-full flex flex-col gap-4 mt-2">
          <input
            autoFocus
            type="tel"
            inputMode="numeric"
            pattern="[0-9]*"
            autoComplete="one-time-code"
            value={code}
            onChange={(e) => handleCodeChange(e.target.value)}
            placeholder="••••••"
            maxLength={OTP_LENGTH}
            className="w-full px-4 py-4 rounded-xl bg-white/[0.04] border border-white/[0.06]
                       text-text text-center text-2xl font-mono tracking-[0.5em]
                       placeholder:text-text-hint/30
                       focus:outline-none focus:border-primary/40 focus:bg-white/[0.06]
                       transition-colors"
          />

          {error && (
            <p className="text-xs text-danger text-center -mt-1">{error}</p>
          )}

          <Button
            variant="primary"
            size="lg"
            fullWidth
            onClick={handleVerify}
            isLoading={isLoading}
            disabled={code.length !== OTP_LENGTH}
          >
            {t('auth.verify', 'Tasdiqlash')}
            <ArrowRight size={18} className="ml-2" />
          </Button>

          <button
            type="button"
            onClick={handleResend}
            disabled={isLoading}
            className="text-xs text-text-hint hover:text-text transition-colors
                       flex items-center justify-center gap-1.5 py-2 disabled:opacity-50"
          >
            <RotateCcw size={12} />
            {t('auth.resendCode', 'Kodni qaytadan olish')}
          </button>
        </div>
      )}
    </div>
  )
}
